/**
 * Every chart has an aria summary and "View as table" (screens §2). The chart is `children`; the table is the same data as text.
 * One control per frame: a page that shows several charts together wraps them in `ChartTablesProvider` and keeps a single toggle of its own,
 * and the frames inside then follow it and print no button of their own.
 */
import { createContext, useContext, useState, type ReactNode } from "react";

export interface ChartTable { readonly caption: string; readonly head: readonly string[]; readonly rows: readonly (readonly string[])[] }

const ChartTablesContext = createContext<boolean | null>(null);

/** Charts inside show as tables (true) or charts (false) together; the page owns the one button that flips it. */
export function ChartTablesProvider({ asTable, children }: { readonly asTable: boolean; readonly children: ReactNode }) {
  return <ChartTablesContext.Provider value={asTable}>{children}</ChartTablesContext.Provider>;
}

/** The page-level "Show as tables" switch a chart sits under (true: tables), or null when no provider wraps it and the chart keeps its own button. */
export function useChartTables(): boolean | null {
  return useContext(ChartTablesContext);
}

/** A chart's data as a table: the caption is read aloud, the first column heads each row. */
export function ChartTableView({ table }: { readonly table: ChartTable }) {
  return (
    <div className="gx-chart-table">
      <table className="gx-table gx-num">
        <caption className="gx-sr">{table.caption}</caption>
        <thead><tr>{table.head.map((h) => <th key={h} scope="col">{h}</th>)}</tr></thead>
        <tbody>{table.rows.map((row) => <tr key={row.join("|")}>{row.map((cell, i) => (i === 0 ? <th key={i} scope="row">{cell}</th> : <td key={i}>{cell}</td>))}</tr>)}</tbody>
      </table>
    </div>
  );
}

export function ChartFrame({ summary, table, children, caption }: { readonly summary: string; readonly table: ChartTable; readonly children: ReactNode; /** The chart's own caption: it stands between the chart and the "View as table" button. */ readonly caption?: ReactNode }) {
  const [own, setOwn] = useState(false);
  const shared = useChartTables();
  const asTable = shared ?? own;
  return (
    <div className="gx-chartframe">
      {asTable ? <ChartTableView table={table} /> : <div role="img" aria-label={summary}>{children}</div>}
      {caption ?? null}
      {shared === null ? (
        <button type="button" className="gx-btn gx-btn--secondary gx-small gx-chart-toggle" aria-pressed={asTable} onClick={() => setOwn((v) => !v)}>
          {asTable ? "View as chart" : "View as table"}
        </button>
      ) : null}
    </div>
  );
}
